import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Phone, Mail, ExternalLink, ChevronRight,
  Building2, Award, ListChecks, Star, TrendingUp, Scale,
  GraduationCap, Users, Monitor, HelpCircle, Settings, BookOpen,
  CheckCircle,
} from "lucide-react";
import { FORMAZIONE_SUBPAGES } from "@/data/formazioneSubpageData";

/* ── mappa nomi icona → componente ── */
const ICONS = {
  Building2, Award, ListChecks, Star, TrendingUp, Scale,
  GraduationCap, Users, Monitor, HelpCircle, Settings, BookOpen, CheckCircle,
};

function SectionTitle({ children }) {
  return (
    <h2 className="text-xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-5">
      {children}
    </h2>
  );
}

/* ── Sezione testo libero ── */
function SezioneText({ s }) {
  return (
    <div>
      <SectionTitle>{s.titolo}</SectionTitle>
      {s.paragrafi.map((p, i) => (
        <p key={i} className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4 text-base">{p}</p>
      ))}
    </div>
  );
}

/* ── Sezione lista puntata ── */
function SezioneLista({ s }) {
  return (
    <div>
      <SectionTitle>{s.titolo}</SectionTitle>
      {s.preambolo && (
        <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4 text-base">{s.preambolo}</p>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
        {s.items.map((item, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
            <div className="w-2 h-2 rounded-full shrink-0 mt-2 bg-[var(--confcom-blue)]" />
            <div>
              {item.titolo && (
                <span className="font-semibold text-[var(--confcom-text-primary)] text-sm">{item.titolo}. </span>
              )}
              {item.testo && (
                <span className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{item.testo}</span>
              )}
              {!item.titolo && !item.testo && (
                <span className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{item}</span>
              )}
            </div>
          </div>
        ))}
      </div>
      {s.nota && (
        <p className="mt-4 text-sm text-[var(--confcom-text-secondary)] italic leading-relaxed">{s.nota}</p>
      )}
    </div>
  );
}

/* ── Sezione steps numerati ── */
function SezioneSteps({ s }) {
  return (
    <div>
      <SectionTitle>{s.titolo}</SectionTitle>
      <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
        {s.items.map((step, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "1rem" }}>
            <div
              style={{
                width: 32, height: 32,
                borderRadius: "50%",
                background: "var(--confcom-blue)",
                color: "#fff",
                fontSize: "0.78rem",
                fontWeight: 800,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
                fontFamily: "var(--font-encode)",
              }}
            >
              {step.numero}
            </div>
            <div>
              <h3 className="text-sm font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-1">
                {step.titolo}
              </h3>
              <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{step.testo}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Sezione FAQ accordion ── */
function SezioneFaq({ s }) {
  return (
    <div>
      <SectionTitle>{s.titolo}</SectionTitle>
      <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
        {s.items.map((faq, i) => (
          <div
            key={i}
            style={{
              borderBottom: i < s.items.length - 1 ? "1px solid var(--confcom-border)" : "none",
              paddingTop: "1rem", paddingBottom: "1rem",
            }}
          >
            <p className="text-sm font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-1">
              {faq.q}
            </p>
            <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{faq.a}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── PAGINA PRINCIPALE ── */
export default function FormazioneSottopaginaPage() {
  const { slug } = useParams();
  const page = FORMAZIONE_SUBPAGES.find((p) => p.slug === slug);

  if (!page) {
    return (
      <main id="main-content" className="py-20 text-center">
        <p className="text-[var(--confcom-text-secondary)]">Pagina non trovata.</p>
        <Link to="/formazione" className="text-[var(--confcom-blue)] underline mt-4 inline-block">
          Torna alla Formazione
        </Link>
      </main>
    );
  }

  return (
    <main id="main-content" data-testid={`formazione-subpage-${page.slug}`}>

      {/* ── Hero ── */}
      <section className="relative h-[300px] md:h-[360px] overflow-hidden">
        <img
          src={page.heroImg}
          alt={page.titolo}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4,30,66,0.82)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full flex flex-col justify-end pb-12">
          <Link
            to="/formazione"
            className="flex w-fit items-center gap-2 text-sm text-white/70 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft size={16} /> Formazione
          </Link>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed'] block mb-3">
            Formazione
          </span>
          <h1 className="text-3xl md:text-4xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight max-w-3xl">
            {page.titolo}
          </h1>
          <p className="mt-3 text-white/65 text-sm max-w-2xl leading-relaxed">{page.intro}</p>
        </div>
      </section>

      {/* ── Corpo ── */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">

            {/* Colonna sinistra 2/3 */}
            <div className="lg:col-span-2" style={{ display: "flex", flexDirection: "column", gap: "3rem" }}>
              {page.sezioni.map((s, i) => {
                if (s.tipo === "text")   return <SezioneText   key={i} s={s} />;
                if (s.tipo === "lista")  return <SezioneLista  key={i} s={s} />;
                if (s.tipo === "steps")  return <SezioneSteps  key={i} s={s} />;
                if (s.tipo === "faq")    return <SezioneFaq    key={i} s={s} />;
                return null;
              })}
            </div>

            {/* Sidebar 1/3 */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl border border-[var(--confcom-border)] p-6 sticky top-[104px] lg:top-[168px] shadow-sm">

                {/* Banner + CTA unificati */}
                <a
                  href={page.contatti.ctaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`cta-consulenza-${page.slug}`}
                  style={{
                    display: "block",
                    background: "var(--confcom-blue)",
                    borderRadius: "0.75rem",
                    padding: "1.5rem",
                    marginBottom: "1.25rem",
                    textDecoration: "none",
                  }}
                >
                  <p style={{ color: "rgba(255,255,255,0.65)", fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: "0.4rem" }}>
                    {page.ctaBanner?.label || "Hai bisogno di supporto?"}
                  </p>
                  <p className="font-['Encode_Sans_Semi_Condensed']" style={{ color: "#fff", fontSize: "1rem", fontWeight: 700, lineHeight: 1.3, marginBottom: "1rem" }}>
                    {page.ctaBanner?.title || "Consulenza gratuita con il nostro team"}
                  </p>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", background: "rgba(255,255,255,0.15)", borderRadius: "0.5rem", padding: "0.65rem 1rem", color: "#fff", fontWeight: 700, fontSize: "0.88rem" }}>
                    Richiedi maggiori informazioni <ExternalLink size={14} />
                  </div>
                </a>

                {/* CTA catalogo extra (solo Finanziata) */}
                {page.ctaExtra && (
                  <a
                    href={page.ctaExtra.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 border border-[var(--confcom-blue)] text-[var(--confcom-blue)] font-medium text-sm py-3 rounded-lg w-full hover:bg-[#EBF5FF] transition-colors mb-4"
                    style={{ textDecoration: "none" }}
                  >
                    {page.ctaExtra.label} <ExternalLink size={13} />
                  </a>
                )}

                <hr className="my-4 border-[var(--confcom-border)]" />

                <h3 className="text-sm font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-4">
                  Informazioni
                </h3>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
                  {(Array.isArray(page.contatti.tel) ? page.contatti.tel : [page.contatti.tel]).map((tel) => (
                    <a
                      key={tel}
                      href={`tel:+39${tel.split("/")[0].trim().replace(/[\s.]/g, "")}`}
                      className="flex items-center gap-3 text-sm text-[var(--confcom-text-secondary)] hover:text-[var(--confcom-blue)] transition-colors"
                      style={{ textDecoration: "none" }}
                    >
                      <Phone size={16} className="text-[var(--confcom-blue)] shrink-0" />
                      {tel}
                    </a>
                  ))}
                  {(Array.isArray(page.contatti.email) ? page.contatti.email : [page.contatti.email]).map((email) => (
                    <a
                      key={email}
                      href={`mailto:${email}`}
                      className="flex items-center gap-3 text-sm text-[var(--confcom-text-secondary)] hover:text-[var(--confcom-blue)] transition-colors"
                      style={{ textDecoration: "none" }}
                    >
                      <Mail size={16} className="text-[var(--confcom-blue)] shrink-0" />
                      {email}
                    </a>
                  ))}
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
